import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { createAuthGuardedStorage, createAuthSlice, type AuthSlice } from "@njbsoft/store";

type AdminStore = AuthSlice;

/** The localStorage key the persist middleware writes — the channel sibling
 *  tabs observe each other's token rotations and sign-outs through. */
export const ADMIN_STORE_PERSIST_KEY = "njbsoft-admin-store";

/** What survives a reload. Lifted out of the persist options so the
 *  guarded storage can be typed on the PERSISTED shape rather than the whole
 *  store — the two must agree, and deriving one from the other is what keeps
 *  them agreeing. */
const partializeSession = (state: AdminStore) => ({
  // Access token persisted alongside refresh token -- see security.md for threat model.
  token: state.token,
  refreshToken: state.refreshToken,
  expiresAt: state.expiresAt,
  // Ordered against what is already persisted before every write.
  rotatedAt: state.rotatedAt,
  user: state.user,
});
type PersistedAdminStore = ReturnType<typeof partializeSession>;

/** The session's persist channel, guarded so no write can move the token pair
 *  BACKWARDS (see `@njbsoft/store`'s auth-persist). */
const authStorage = createAuthGuardedStorage<PersistedAdminStore>();

export const useAdminStore = create<AdminStore>()(
  devtools(
    persist(
      (...a) => ({
        ...createAuthSlice(...a),
      }),
      {
        name: ADMIN_STORE_PERSIST_KEY,
        storage: authStorage,
        partialize: partializeSession,
        merge: (persisted, current) => ({
          ...current,
          ...(persisted as Partial<AdminStore>),
          isAuthenticated: !!(persisted as Partial<AdminStore>)?.refreshToken,
        }),
      },
    ),
    { name: "SAMS v2 Admin", enabled: import.meta.env.DEV },
  ),
);

// The adapter refuses a write that would move the token pair backwards and
// hands back the pair that survived; this catches THIS tab's memory up to it,
// so the context that lost the race is not left holding a spent pair to replay.
authStorage.onKeptNewer((tokens) => {
  useAdminStore.getState().adoptTokens(tokens);
});
